import { Check, Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatCurrency } from '../../lib/formatters'
import { cn } from '../../lib/utils'
import { InsuranceIndicationStatus } from '../../models/insuranceTypes'
import type { CarrierCard } from './carrierCard'

// Round to the nearest $10 so the estimated range reads cleanly
// ($8,500 – $15,900 rather than $8,516 – $15,896).
const money = (n: number): string =>
  formatCurrency(Math.round(n / 10) * 10, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

interface CarrierResultCardProps {
  card: CarrierCard
  rangeBounds: { min: number; max: number } | null
  billing?: 'annual' | 'monthly'
}

export function CarrierResultCard({ card, rangeBounds, billing = 'annual' }: CarrierResultCardProps) {
  const declined =
    card.status === InsuranceIndicationStatus.DECLINED ||
    card.status === InsuranceIndicationStatus.UNAVAILABLE ||
    card.status === InsuranceIndicationStatus.ERROR

  const monthly = billing === 'monthly'
  const divisor = monthly ? 12 : 1
  const truckCount = card.truckCount ?? 0

  return (
    <div className={cn('bg-card flex flex-col rounded-xl border')}>
      <div className="flex items-center gap-3 p-4">
        <img
          src={card.iconUrl}
          alt={card.iconAlt}
          className="border-ink-50 size-11 shrink-0 rounded-full border object-contain"
        />
        <div className="flex flex-col">
          <span className="text-foreground text-lg font-bold">{card.vendorName}</span>
          {card.tagline && <span className="text-muted-foreground text-sm">{card.tagline}</span>}
        </div>
        <span
          className={cn(
            'ml-auto flex items-center gap-1 text-sm font-semibold',
            declined ? 'text-muted-foreground' : 'text-accent-green'
          )}
        >
          {!declined && <Check className="size-4" />}
          {declined ? 'Not eligible' : 'Likely eligible'}
        </span>
      </div>

      {declined ? (
        <div className="text-muted-foreground border-t-ink-50 border-t px-4 py-3 text-xs">
          {card.declineReason || 'Not eligible for this insurance provider.'}
        </div>
      ) : (
        <div className="border-t-ink-50 flex flex-col gap-3 border-t p-4">
          {card.perTruckLow != null && card.perTruckHigh != null && (
            <>
              <div>
                <span className="text-foreground text-2xl font-bold">
                  {money(card.perTruckLow / divisor)} – {money(card.perTruckHigh / divisor)}
                </span>
                <span className="text-ink-500"> truck / {monthly ? 'month' : 'year'} estimated</span>
              </div>
              {card.fleetLow != null && card.fleetHigh != null && truckCount > 1 && (
                <div className="text-ink-300 text-sm">
                  {money(card.fleetLow / divisor)} – {money(card.fleetHigh / divisor)} per{' '}
                  {monthly ? 'month' : 'year'} for your {truckCount} truck fleet
                </div>
              )}
              {rangeBounds && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground text-xs font-medium">
                      Compared to other estimates
                    </span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="iconOnly"
                          size="icon-sm"
                          aria-label="What does this bar mean?"
                          className="text-muted-foreground hover:text-foreground size-4"
                        >
                          <Info className="size-3.5" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 text-sm">
                        This bar shows how this estimated price range compares to estimates from other
                        insurance providers. It spans from your lowest estimated price to your highest, with
                        this insurance provider&apos;s estimated range highlighted in between.
                      </PopoverContent>
                    </Popover>
                  </div>
                  <RangeBar bounds={rangeBounds} low={card.perTruckLow} high={card.perTruckHigh} />
                </div>
              )}
            </>
          )}
          {card.carrierNote && (
            <div className="text-muted-foreground border-t-ink-50 border-t pt-3 text-xs">
              {card.carrierNote}
            </div>
          )}
          {card.carrierPublicNote && (
            <div className="text-muted-foreground border-t-ink-50 border-t pt-3 text-xs">
              {card.carrierPublicNote}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RangeBar({
  bounds,
  low,
  high,
}: {
  bounds: { min: number; max: number }
  low: number
  high: number
}) {
  const span = Math.max(1, bounds.max - bounds.min)
  const left = ((low - bounds.min) / span) * 100
  const width = Math.max(4, ((high - low) / span) * 100)
  return (
    <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
      <div
        className="bg-brand-700 h-full rounded-full"
        style={{ marginLeft: `${left}%`, width: `${width}%` }}
      />
    </div>
  )
}
